import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { CompanyOnboardingStepKey } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_DOCS_URL } from '~constants/consts';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { canManageCompanyOnboarding } from '~hooks/useCompanyOnboarding/companyOnboardingAccess';
import { CompanyOnboardingStep } from '~hooks/useCompanyOnboarding/models';
import useCompanyOnboarding from '~hooks/useCompanyOnboarding/useCompanyOnboarding';
import useCompanyOnboardingProgressQuery from '~queries/useCompanyOnboardingProgressQuery';
import {
  companyOnboardingChecklistPath,
  companyOnboardingStepPath,
} from '~router/routePaths';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  CompletionIcon,
  CompletionToggleButton,
  Eyebrow,
  OnboardingCard,
  OnboardingLink,
  onboardingLinkSx,
  ProgressQueryStatus,
  SecondaryText,
  TitleText,
} from './CompanyOnboardingPrimitives';
import CompanyOnboardingStepContent from './CompanyOnboardingStepContent';
import {
  companyOnboardingSteps,
  isCompanyOnboardingStepKey,
} from './companyOnboardingSteps';

const PROSE_MEASURE = '68ch';

const RAIL_WIDTH = '200px';

const ASIDE_WIDTH = '260px';

const isOfferedStepKey = (
  stepKey: string | undefined,
  stepKeys: CompanyOnboardingStepKey[],
): stepKey is CompanyOnboardingStepKey =>
  isCompanyOnboardingStepKey(stepKey) && stepKeys.includes(stepKey);

const StepAside = ({ stepKey }: { stepKey: CompanyOnboardingStepKey }) => {
  const moduleKey = (key: string) => `company_onboarding.modules.${stepKey}.${key}`;

  return (
    <Box sx={{ flexShrink: 0, width: ASIDE_WIDTH }}>
      <Eyebrow>{t('company_onboarding.source_heading')}</Eyebrow>
      <TitleText sx={{ mt: 1 }}>{t(moduleKey('source_title'))}</TitleText>
      <SecondaryText sx={{ mt: 1 }}>{t(moduleKey('source_primary'))}</SecondaryText>
      <Typography sx={{ mt: 1 }}>
        {t('company_onboarding.source_ask', { owner: t(moduleKey('source_owner')) })}
      </Typography>
      <Eyebrow sx={{ mt: 2 }}>{t('company_onboarding.source_other')}</Eyebrow>
      <SecondaryText>{t(moduleKey('source_alternate'))}</SecondaryText>
      <Eyebrow sx={{ mt: 2 }}>{t('company_onboarding.source_request')}</Eyebrow>
      <SecondaryText>{t(moduleKey('source_request'))}</SecondaryText>
      <OnboardingCard data-test-id="company-onboarding-tip-card" sx={{ mt: 3, p: 2 }}>
        <TitleText>{t(moduleKey('tip_title'))}</TitleText>
        <SecondaryText sx={{ mt: 1 }}>{t(moduleKey('tip_body'))}</SecondaryText>
        <TitleText sx={{ mt: 2 }}>{t(moduleKey('tip_secondary_title'))}</TitleText>
        <SecondaryText sx={{ mt: 0.5 }}>
          {t(moduleKey('tip_secondary_body'))}
        </SecondaryText>
        <Link
          href={`${TREAD_DOCS_URL}/${companyOnboardingSteps[stepKey].docsPath}`}
          rel="noopener"
          sx={(theme) => ({ ...onboardingLinkSx(theme), display: 'inline-block', mt: 2 })}
          target="_blank"
          variant="body1"
        >
          {t('company_onboarding.tip_guide_link')}
        </Link>
      </OnboardingCard>
    </Box>
  );
};

interface StepRailProps {
  completedStepsByKey: Map<CompanyOnboardingStepKey, CompanyOnboardingStep>;
  currentStepKey: CompanyOnboardingStepKey;
  stepKeys: CompanyOnboardingStepKey[];
}

const StepRail = ({ completedStepsByKey, currentStepKey, stepKeys }: StepRailProps) => (
  <Box sx={{ flexShrink: 0, width: RAIL_WIDTH }}>
    <Eyebrow>{t('company_onboarding.rail_heading')}</Eyebrow>
    <Box sx={{ mt: 1 }}>
      {stepKeys.map((stepKey, index) => {
        const isCurrent = stepKey === currentStepKey;
        const isCompleted = completedStepsByKey.has(stepKey);

        return (
          <OnboardingLink
            key={stepKey}
            data-test-id={`company-onboarding-rail-${stepKey}`}
            to={companyOnboardingStepPath(stepKey)}
            sx={(theme) => ({
              alignItems: 'center',
              backgroundColor: isCurrent ? theme.semantic.surfaceOverlay : 'transparent',
              borderRadius: 1,
              color: 'text.primary',
              display: 'flex',
              fontSize: 13,
              fontWeight: isCurrent ? 600 : 400,
              gap: 1,
              px: 1,
              py: 0.75,
              textDecoration: 'none',
            })}
          >
            <CompletionIcon isCompleted={isCompleted} sx={{ fontSize: 16 }} />
            {index + 1}. {t(`company_onboarding.modules.${stepKey}.title`)}
          </OnboardingLink>
        );
      })}
    </Box>
  </Box>
);

const CompanyOnboardingStepPage = observer(() => {
  const { stepKey } = useParams<{ stepKey: string }>();
  const navigate = useNavigate();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const canManageOnboarding = canManageCompanyOnboarding(userStore);
  const { completedStepsByKey, hasLoaded, isError, isPending, stepKeys } =
    useCompanyOnboardingProgressQuery({ companyId });
  const { toggleStep } = useCompanyOnboarding();
  const completedStepsByKeyRef = useRef(completedStepsByKey);
  completedStepsByKeyRef.current = completedStepsByKey;

  useEffect(() => {
    if (hasLoaded && isOfferedStepKey(stepKey, stepKeys)) {
      AnalyticsController.track('company_onboarding_step_viewed', {
        is_completed: completedStepsByKeyRef.current.has(stepKey),
        step_key: stepKey,
        step_number: stepKeys.indexOf(stepKey) + 1,
      });
    }
  }, [hasLoaded, stepKey, stepKeys]);

  if (!isOfferedStepKey(stepKey, stepKeys)) {
    return <Navigate to={companyOnboardingChecklistPath} replace />;
  }

  const stepIndex = stepKeys.indexOf(stepKey);
  const isCompleted = completedStepsByKey.has(stepKey);

  const goBack = () => {
    const previousStepKey = stepKeys[stepIndex - 1];

    navigate(
      previousStepKey
        ? companyOnboardingStepPath(previousStepKey)
        : companyOnboardingChecklistPath,
    );
  };

  const goForward = () => {
    const nextIncompleteStepKey = stepKeys
      .slice(stepIndex + 1)
      .find((key) => !completedStepsByKey.has(key));

    navigate(
      nextIncompleteStepKey
        ? companyOnboardingStepPath(nextIncompleteStepKey)
        : companyOnboardingChecklistPath,
    );
  };

  return (
    <Box>
      <ProgressQueryStatus isError={isError} isPending={isPending} />
      {hasLoaded ? (
        <Box sx={{ display: 'flex', gap: 4, px: 3, py: 3 }}>
          <StepRail
            completedStepsByKey={completedStepsByKey}
            currentStepKey={stepKey}
            stepKeys={stepKeys}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 2 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Eyebrow>
                  {t('company_onboarding.eyebrow', {
                    current: stepIndex + 1,
                    phase: t(
                      `company_onboarding.phases.${companyOnboardingSteps[stepKey].phase}`,
                    ),
                    total: stepKeys.length,
                  })}
                </Eyebrow>
                <Typography variant="page-title" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {t(`company_onboarding.modules.${stepKey}.headline`)}
                </Typography>
                <SecondaryText sx={{ maxWidth: PROSE_MEASURE, mt: 1 }}>
                  {t(`company_onboarding.modules.${stepKey}.body`)}
                </SecondaryText>
              </Box>
              <CompletionToggleButton
                disabled={!canManageOnboarding}
                isCompleted={isCompleted}
                onToggle={() => toggleStep(companyId, stepKey)}
                testId="company-onboarding-step-toggle"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <CompanyOnboardingStepContent
                  key={`${stepKey}-${companyId}`}
                  companyId={companyId}
                  stepKey={stepKey}
                />
              </Box>
              <StepAside stepKey={stepKey} />
            </Box>
            <Box
              sx={(theme) => ({
                alignItems: 'center',
                backgroundColor: 'background.default',
                borderTop: `1px solid ${theme.palette.divider}`,
                bottom: 'var(--tread-agent-dock-height, 0px)',
                display: 'flex',
                gap: 2,
                mt: 5,
                position: 'sticky',
                py: 2,
                zIndex: theme.zIndex.appBar,
              })}
            >
              <SmallButton color="secondary" onClick={goBack} variant="outlined">
                {t('company_onboarding.actions.back')}
              </SmallButton>
              <Box sx={{ flexGrow: 1 }} />
              <OnboardingLink to={companyOnboardingChecklistPath}>
                {t('company_onboarding.actions.do_this_later')}
              </OnboardingLink>
              <SmallButton
                color="brandV2Yellow"
                data-test-id="company-onboarding-step-continue"
                onClick={goForward}
                variant="contained"
              >
                {t('company_onboarding.actions.continue')}
              </SmallButton>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
});

export default CompanyOnboardingStepPage;
